import i18n from 'i18next';
import { saveAs } from 'file-saver';

import { alertActions } from '../../alert';
import { machineCsvConstants } from './machineCsv.constants';
import * as machineCsvService from './machineCsv.service';
import { machinesActions } from './machines.actions';
import {
  buildExportParams, buildExportFileName, exportErrorMessage, readFileError
} from './components/MachineCsv/helpers';

export const machineCsvActions = {
  getCsvCatalog,
  exportMachinesCsv,
  previewMachinesCsvImport,
  applyMachinesCsvImport,
  resetMachinesCsvImport
};

function getCsvCatalog() {
  const request = () => ({ type: machineCsvConstants.GET_CSV_CATALOG_REQUEST });
  const success = catalog => ({ type: machineCsvConstants.GET_CSV_CATALOG_SUCCESS, catalog });
  const failure = error => ({ type: machineCsvConstants.GET_CSV_CATALOG_FAILURE, error });

  // No alert on failure: the dialog that asked shows the error and a Retry.
  return (dispatch) => {
    dispatch(request());
    return machineCsvService.getCsvCatalog().then(
      catalog => dispatch(success(catalog)),
      error => dispatch(failure(error))
    );
  };
}

// Resolves true when the file was saved, so the dialog knows to close.
function exportMachinesCsv(columns, scope) {
  const request = () => ({ type: machineCsvConstants.EXPORT_CSV_REQUEST });
  const success = () => ({ type: machineCsvConstants.EXPORT_CSV_SUCCESS });
  const failure = error => ({ type: machineCsvConstants.EXPORT_CSV_FAILURE, error });

  return (dispatch, getState) => {
    const { machines, currentAccount } = getState();
    dispatch(request());
    return machineCsvService.exportMachinesCsv(buildExportParams(machines, columns, scope)).then(
      (file) => {
        // The backend names the file; the local name is only a fallback.
        saveAs(file.blob, file.fileName || buildExportFileName(currentAccount && currentAccount.name, new Date()));
        dispatch(success());
        return true;
      },
      (error) => {
        dispatch(failure(error));
        dispatch(alertActions.error(exportErrorMessage(i18n.t.bind(i18n), error)));
        return false;
      }
    );
  };
}

// Every preview and apply carries an id. The reducer keeps the newest and
// drops a response whose id is no longer current, so a response that lands
// after the dialog was closed or another file was chosen is never shown.
let lastRequestId = 0;
const nextRequestId = () => {
  lastRequestId += 1;
  return lastRequestId;
};

function previewMachinesCsvImport(file) {
  const request = (fileName, requestId) =>
    ({ type: machineCsvConstants.PREVIEW_IMPORT_REQUEST, fileName, requestId });
  const success = (preview, requestId) =>
    ({ type: machineCsvConstants.PREVIEW_IMPORT_SUCCESS, preview, requestId });
  const failure = (fileError, requestId) =>
    ({ type: machineCsvConstants.PREVIEW_IMPORT_FAILURE, fileError, requestId });

  return (dispatch) => {
    const requestId = nextRequestId();
    dispatch(request(file.name, requestId));
    return machineCsvService.previewMachinesCsvImport(file).then(
      preview => dispatch(success(preview, requestId)),
      error => dispatch(failure(readFileError(error), requestId))
    );
  };
}

function applyMachinesCsvImport(changes) {
  const request = requestId => ({ type: machineCsvConstants.APPLY_IMPORT_REQUEST, requestId });
  const success = (result, requestId) =>
    ({ type: machineCsvConstants.APPLY_IMPORT_SUCCESS, result, requestId });
  const failure = (problems, error, requestId) =>
    ({ type: machineCsvConstants.APPLY_IMPORT_FAILURE, problems, error, requestId });

  return (dispatch, getState) => {
    const requestId = nextRequestId();
    const refreshList = () =>
      dispatch(machinesActions.setMachineSearchParams(getState().machines.searchParams));
    dispatch(request(requestId));
    return machineCsvService.applyMachinesCsvImport(changes).then(
      (result) => {
        dispatch(success(result, requestId));
        // Refresh the list the user is looking at, filters and all.
        refreshList();
      },
      (error) => {
        if (error && Array.isArray(error.problems) && error.problems.length > 0) {
          dispatch(failure(error.problems, null, requestId));
          return;
        }
        dispatch(failure(null, error, requestId));
        if (!error || error.code === 0 || error.code >= 500) {
          // No answer, or a server or gateway error (a 502 or 504 can come
          // back while the apply still commits): it may have been saved. Show
          // what the site holds now rather than inviting a blind retry.
          refreshList();
          dispatch(alertActions.error(i18n.t('assets.csv.apply.unconfirmed', {
            defaultValue: 'We could not confirm the import finished. The list has been refreshed: check it, and export again before importing more changes.'
          })));
          return;
        }
        // A 4xx is a refusal: nothing was written. Never the raw body.
        dispatch(alertActions.error(i18n.t('assets.csv.apply.failed', {
          defaultValue: 'The import could not be applied and nothing was changed. Please try again.'
        })));
      }
    );
  };
}

function resetMachinesCsvImport() {
  return { type: machineCsvConstants.RESET_IMPORT };
}
